import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminRanks.module.css';
import axiosClient from '../../api/axiosClient';

interface RankDef { level: number; name: string; selfUsd: number; teamUsd: number; rewardUsd: number }
interface Holder { id: string; full_name: string; email: string; rank_level: number; rank_name: string; claimed_count: number; total_reward_usd: string }
interface Period {
  id: string; period_key: string; net_profit_usd: string; pool_usd: string; promoter_count: number;
  share_usd: string; created_by_name: string | null; created_at: string; claimed_count: number;
}
interface Overview {
  usdRate: number; brandClubPct: number; ranks: RankDef[];
  rateHistory: { old_value: string; new_value: string; changed_at: string; changed_by_name: string | null }[];
  counts: { rank_level: number; holders: number }[]; holders: Holder[]; periods: Period[];
}

interface MsAward { id: string; userId: string; userName: string; userEmail: string; directName: string; tierLabel: string; pct: number; amountInr: number; status: string; awardedAt: string }

const usd = (n: number | string) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const AdminRanks: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [rate, setRate] = useState('');
  const [periodKey, setPeriodKey] = useState('');
  const [profit, setProfit] = useState('');
  const [busy, setBusy] = useState(false);
  const [ms, setMs] = useState<MsAward[]>([]);

  const load = () => {
    axiosClient.get('/admin/ranks').then(({ data }) => { setData(data); setRate(String(data.usdRate)); }).catch((err) => {
      toast.error(err.response?.data?.message || 'Could not load rank data.');
    });
  };
  const loadMs = () => { axiosClient.get('/admin/milestones').then(({ data }) => setMs(data.awards || [])).catch(() => {}); };
  useEffect(() => { load(); loadMs(); }, []);

  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try { const res = await fn(); toast.success(typeof ok === 'string' ? ok : 'Done'); return res; }
    catch (err: any) { toast.error(err.response?.data?.message || 'Something went wrong.'); }
    finally { setBusy(false); }
  };

  const saveRate = async () => { await run(() => axiosClient.patch('/admin/ranks/usd-rate', { rate }), 'USD rate saved.'); load(); };
  const recalc = async () => {
    const res = await run(() => axiosClient.post('/admin/ranks/recalculate'), 'Ranks recalculated.');
    if (res) toast.info(`Checked ${res.data.checked} users, ${res.data.awarded} new rank(s) awarded.`);
    load();
  };
  const recalcMs = async () => {
    const res = await run(() => axiosClient.post('/admin/milestones/recalculate'), 'Big investment bonus rechecked.');
    if (res) toast.info(`Checked ${res.data.checked} referrals, ${res.data.created} new bonus(es).`);
    loadMs();
  };
  const addPeriod = async () => {
    const res = await run(() => axiosClient.post('/admin/ranks/brand-club', { periodKey, netProfitUsd: profit }), 'Brand Club profit shared.');
    if (res) { setPeriodKey(''); setProfit(''); }
    load();
  };

  const poolPreview = Number(profit) > 0 ? (Number(profit) * (data?.brandClubPct || 5)) / 100 : 0;

  return (
    <AdminShell>
      <PageHeader title="Ranks & Brand Club" subtitle="Advisory Board ranks, USD rate and Brand Club profit sharing." action={<AdminWalletBadge />} />
      <div className={panelStyles.page}>
        {data && (
          <>
            <div className={styles.card}>
              <h3>USD rate</h3>
              <div className={styles.hint}>1 USD = how many INR. Rank amounts are in USD. Rewards already earned keep the rate they were earned at.</div>
              <div className={styles.row}>
                <input className={styles.input} value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" />
                <button className={styles.btn} disabled={busy} onClick={saveRate}>Save rate</button>
                <button className={styles.ghost} disabled={busy} onClick={recalc}>Recalculate all ranks</button>
              </div>
              {data.rateHistory.length > 0 && (
                <div className={styles.small}>
                  {data.rateHistory.slice(0, 5).map((h, i) => (
                    <div key={i}>{h.old_value} → <b>{h.new_value}</b> · {h.changed_by_name || 'admin'} · {new Date(h.changed_at).toLocaleString()}</div>
                  ))}
                </div>
              )}
              <div className={styles.small}>Use "Recalculate all ranks" once after setting the rate, so existing users get the rank they already qualify for. After that ranks update automatically when an investment is approved or cancelled.</div>
            </div>

            <div className={styles.card}>
              <h3>Rank rules</h3>
              <div className={styles.wrap}>
                <table className={styles.table}>
                  <thead><tr><th>Rank</th><th>Self</th><th>Team</th><th>One-time reward</th><th>Holders</th></tr></thead>
                  <tbody>
                    {data.ranks.map((r) => (
                      <tr key={r.level}>
                        <td>{r.level}. {r.name}</td><td>{usd(r.selfUsd)}</td><td>{usd(r.teamUsd)}</td><td>{usd(r.rewardUsd)}</td>
                        <td>{data.counts.find((c) => c.rank_level === r.level)?.holders || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.card}>
              <h3>Brand Club</h3>
              <div className={styles.hint}>
                Enter the company net profit of a period. {data.brandClubPct}% becomes the pool and is shared equally among all Global Promoters. A period can be entered only once.
              </div>
              <div className={styles.row}>
                <input className={styles.input} placeholder="Period e.g. 2026-10 or 2026-Q4" value={periodKey} onChange={(e) => setPeriodKey(e.target.value)} />
                <input className={styles.input} placeholder="Net profit in USD" value={profit} onChange={(e) => setProfit(e.target.value)} inputMode="decimal" />
                <button className={styles.btn} disabled={busy || !periodKey || !profit} onClick={addPeriod}>Share profit</button>
              </div>
              {poolPreview > 0 && <div className={styles.small}>Pool will be {usd(poolPreview)}.</div>}
              {data.periods.length > 0 && (
                <div className={styles.wrap}>
                  <table className={styles.table}>
                    <thead><tr><th>Period</th><th>Net profit</th><th>Pool</th><th>Promoters</th><th>Each</th><th>Claimed</th><th>Entered by</th></tr></thead>
                    <tbody>
                      {data.periods.map((p) => (
                        <tr key={p.id}>
                          <td>{p.period_key}</td><td>{usd(p.net_profit_usd)}</td><td>{usd(p.pool_usd)}</td><td>{p.promoter_count}</td>
                          <td>{usd(p.share_usd)}</td><td>{p.claimed_count}/{p.promoter_count}</td>
                          <td>{p.created_by_name || 'admin'} · {new Date(p.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3>Big investment bonus</h3>
              <div className={styles.hint}>When a direct referral's total investment reaches ₹25 Lac / ₹50 Lac / ₹125 Lac, the person above earns 3% / 4% / 5% once. It appears automatically when an investment is approved. Use the button once to catch up existing users.</div>
              <div className={styles.row}><button className={styles.ghost} disabled={busy} onClick={recalcMs}>Recheck all referrals</button></div>
              {ms.length === 0 && <div className={styles.small}>No bonus earned yet.</div>}
              {ms.length > 0 && (
                <div className={styles.wrap}>
                  <table className={styles.table}>
                    <thead><tr><th>Earned by</th><th>Direct member</th><th>Level</th><th>Rate</th><th>Amount</th><th>Status</th></tr></thead>
                    <tbody>
                      {ms.map((m) => (
                        <tr key={m.id}>
                          <td><span className={styles.link} onClick={() => navigate(`/admin/users/${m.userId}`)}>{m.userName}</span><div className={styles.small}>{m.userEmail}</div></td>
                          <td>{m.directName}</td><td>{m.tierLabel}</td><td>{m.pct}%</td>
                          <td>₹{Number(m.amountInr).toLocaleString('en-IN')}</td><td>{m.status === 'claimed' ? 'Claimed' : 'Ready'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3>Rank holders</h3>
              {data.holders.length === 0 && <div className={styles.hint}>Nobody has a rank yet.</div>}
              {data.holders.length > 0 && (
                <div className={styles.wrap}>
                  <table className={styles.table}>
                    <thead><tr><th>User</th><th>Rank</th><th>Rewards earned</th><th>Claimed</th></tr></thead>
                    <tbody>
                      {data.holders.map((h) => (
                        <tr key={h.id}>
                          <td><span className={styles.link} onClick={() => navigate(`/admin/users/${h.id}`)}>{h.full_name}</span><div className={styles.small}>{h.email}</div></td>
                          <td>{h.rank_name}</td><td>{usd(h.total_reward_usd)}</td><td>{h.claimed_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminRanks;